const DRAFT_PREFIX = 'form_draft_';
const DRAFT_TIMESTAMP_PREFIX = 'form_draft_timestamp_';
const FORM_SESSIONS_PREFIX = 'form_sessions_';

export const draftStorageService = {
  /**
   * Generate a new session GUID
   * @returns {string} UUID v4
   */
  generateSessionGuid: () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  },

  /**
   * Save draft using session GUID
   * @param {string} sessionGuid - The session GUID
   * @param {string} formId - The form ID (for indexing)
   * @param {object} draftData - The draft data to save
   */
  saveDraft: (sessionGuid, formId, draftData) => {
    const key = `${DRAFT_PREFIX}${sessionGuid}`;
    const timestampKey = `${DRAFT_TIMESTAMP_PREFIX}${sessionGuid}`;

    console.log('💾 SAVE DRAFT:', {
      sessionGuid,
      formId,
      dataKeys: Object.keys(draftData),
      dataSize: JSON.stringify(draftData).length
    });

    try {
      localStorage.setItem(key, JSON.stringify(draftData));
      localStorage.setItem(timestampKey, new Date().toISOString());

      // Track this session GUID under the formId
      const sessionsKey = `${FORM_SESSIONS_PREFIX}${formId}`;
      const sessions = JSON.parse(localStorage.getItem(sessionsKey) || '[]');
      if (!sessions.includes(sessionGuid)) {
        sessions.push(sessionGuid);
        localStorage.setItem(sessionsKey, JSON.stringify(sessions));
        console.log('📝 Added sessionGuid to formId sessions list:', { formId, sessionGuid });
      }
      console.log('✅ Draft saved successfully');
    } catch (error) {
      console.error('❌ Failed to save draft:', error);
    }
  },

  /**
   * Get draft by session GUID
   * @param {string} sessionGuid - The session GUID
   * @returns {object|null} The draft data or null
   */
  getDraft: (sessionGuid) => {
    const key = `${DRAFT_PREFIX}${sessionGuid}`;
    const saved = localStorage.getItem(key);

    console.log('📖 GET DRAFT:', {
      sessionGuid,
      key,
      exists: !!saved,
      size: saved?.length || 0
    });

    if (!saved) {
      console.log('⚠️ No draft found for sessionGuid:', sessionGuid);
      return null;
    }

    try {
      const parsed = JSON.parse(saved);
      console.log('✅ Draft loaded successfully:', {
        dataKeys: Object.keys(parsed),
        dataSize: JSON.stringify(parsed).length
      });
      return parsed;
    } catch (e) {
      console.error('❌ Failed to parse draft:', e);
      return null;
    }
  },

  /**
   * Get draft timestamp by session GUID
   * @param {string} sessionGuid - The session GUID
   * @returns {string|null} ISO 8601 timestamp or null
   */
  getDraftTimestamp: (sessionGuid) => {
    const timestampKey = `${DRAFT_TIMESTAMP_PREFIX}${sessionGuid}`;
    return localStorage.getItem(timestampKey);
  },

  /**
   * Delete draft by session GUID
   * @param {string} sessionGuid - The session GUID
   * @param {string} formId - The form ID (for cleanup)
   */
  deleteDraft: (sessionGuid, formId) => {
    const key = `${DRAFT_PREFIX}${sessionGuid}`;
    const timestampKey = `${DRAFT_TIMESTAMP_PREFIX}${sessionGuid}`;

    localStorage.removeItem(key);
    localStorage.removeItem(timestampKey);

    // Remove from sessions list
    const sessionsKey = `${FORM_SESSIONS_PREFIX}${formId}`;
    const sessions = JSON.parse(localStorage.getItem(sessionsKey) || '[]');
    const filtered = sessions.filter(guid => guid !== sessionGuid);
    if (filtered.length > 0) {
      localStorage.setItem(sessionsKey, JSON.stringify(filtered));
    } else {
      localStorage.removeItem(sessionsKey);
    }
  },

  /**
   * Check if draft exists for a session GUID
   * @param {string} sessionGuid - The session GUID
   * @returns {boolean} True if draft exists
   */
  hasDraft: (sessionGuid) => {
    const key = `${DRAFT_PREFIX}${sessionGuid}`;
    return localStorage.getItem(key) !== null;
  },

  /**
   * List all draft sessions for a formId
   * @param {string} formId - The form ID
   * @returns {Array} Array of { sessionGuid, data, timestamp }
   */
  listDraftsForForm: (formId) => {
    const sessionsKey = `${FORM_SESSIONS_PREFIX}${formId}`;
    const sessionGuids = JSON.parse(localStorage.getItem(sessionsKey) || '[]');

    console.log('📋 LIST DRAFTS FOR FORM:', {
      formId,
      sessionsKey,
      sessionGuids,
      count: sessionGuids.length
    });

    const drafts = sessionGuids.map(guid => {
      const key = `${DRAFT_PREFIX}${guid}`;
      const timestampKey = `${DRAFT_TIMESTAMP_PREFIX}${guid}`;

      let data = null;
      const saved = localStorage.getItem(key);
      if (saved) {
        try {
          data = JSON.parse(saved);
          console.log('  ✅ Draft found for guid:', guid, 'keys:', Object.keys(data));
        } catch (e) {
          console.error('  ❌ Failed to parse draft for guid:', guid, e);
        }
      } else {
        console.log('  ⚠️ No data for guid:', guid);
      }

      const timestamp = localStorage.getItem(timestampKey);
      return { sessionGuid: guid, data, timestamp };
    }).filter(draft => draft.data !== null); // Filter out corrupted/deleted drafts

    console.log('📋 Total drafts found:', drafts.length);
    return drafts;
  },

  /**
   * List all drafts (for future "My Drafts" page)
   * @returns {Array} Array of draft objects
   */
  listDrafts: () => {
    const drafts = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(DRAFT_PREFIX)) {
        const sessionGuid = key.replace(DRAFT_PREFIX, '');
        try {
          const data = JSON.parse(localStorage.getItem(key));
          const timestamp = localStorage.getItem(`${DRAFT_TIMESTAMP_PREFIX}${sessionGuid}`);

          drafts.push({ sessionGuid, data, timestamp });
        } catch (error) {
          console.error(`Failed to parse draft for session ${sessionGuid}:`, error);
        }
      }
    }

    return drafts;
  }
};
